import React, { useCallback, useEffect, useMemo, useState } from 'react';
import BaseSelect from '../../Base/BaseSelect';
import useAdicionalCatalogService, {
  ManagementItem,
  SuperintendencyItem,
  AreaItem,
  ElAbraGroupItem,
  AdicionalEvents,
} from '../../../services/useAdicionalCatalogService';
import useServicesService from '../../../services/useServicesService';
import type { ServiceResponse } from '../../../hooks/useFetch';
import showToast from '../../../utils/toast';
import ElAbraGroupModal from './ElAbraGroupModal';
import './ServiceAdicionalPanel.css';

const compareEs = (a: string, b: string) =>
  String(a ?? '').localeCompare(String(b ?? ''), 'es', { sensitivity: 'base' });

export interface AdicionalInitialValues {
  management?: number | null;
  superintendency?: number | null;
  area?: number | null;
  service_reason?: number | null;
  service_reason_text?: string | null;
}

interface ServiceAdicionalPanelProps {
  serviceId: number;
  initialValues?: AdicionalInitialValues | null;
  onSaved?: () => void;
}

const ServiceAdicionalPanel: React.FC<ServiceAdicionalPanelProps> = ({
  serviceId,
  initialValues,
  onSaved,
}) => {
  const [managements, setManagements] = useState<ManagementItem[]>([]);
  const [superintendencies, setSuperintendencies] = useState<SuperintendencyItem[]>([]);
  const [areas, setAreas] = useState<AreaItem[]>([]);
  const [elAbraGroups, setElAbraGroups] = useState<ElAbraGroupItem[]>([]);

  const [selectedShortcut, setSelectedShortcut] = useState<number | ''>('');

  const [selectedManagement, setSelectedManagement] = useState<number | ''>('');
  const [selectedSuperintendency, setSelectedSuperintendency] = useState<number | ''>('');
  const [selectedArea, setSelectedArea] = useState<number | ''>('');
  const [serviceReasonText, setServiceReasonText] = useState('');
  /** Valor de catálogo existente (ya no se edita en UI); se preserva al guardar. */
  const [preservedServiceReason, setPreservedServiceReason] = useState<number | null>(null);

  const [groupModalOpen, setGroupModalOpen] = useState(false);

  const {
    fetching,
    listManagements,
    listSuperintendencies,
    listAreas,
    listElAbraGroups,
    storeElAbraGroup,
  } = useAdicionalCatalogService();
  const { updateAdicional, fetching: servicesFetching } = useServicesService();

  const handleList = useCallback(
    (r: ServiceResponse, key: 'managements' | 'superintendencies' | 'areas' | 'el_abra_groups') => {
      const data = r?.data;
      if (!data) return;
      const list =
        data[key] ??
        data.managements ??
        data.superintendencies ??
        data.areas ??
        data.el_abra_groups;
      if (Array.isArray(list)) {
        if (key === 'managements') setManagements(list);
        else if (key === 'superintendencies') setSuperintendencies(list);
        else if (key === 'areas') setAreas(list);
        else if (key === 'el_abra_groups') setElAbraGroups(list);
      }
    },
    []
  );

  const loadAll = useCallback(() => {
    listManagements(1, { onSuccess: r => handleList(r, 'managements') } as AdicionalEvents);
    listSuperintendencies(1, { onSuccess: r => handleList(r, 'superintendencies') } as AdicionalEvents);
    listAreas(1, { onSuccess: r => handleList(r, 'areas') } as AdicionalEvents);
    listElAbraGroups(1, { onSuccess: r => handleList(r, 'el_abra_groups') } as AdicionalEvents);
  }, [listManagements, listSuperintendencies, listAreas, listElAbraGroups, handleList]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  useEffect(() => {
    if (initialValues) {
      setSelectedManagement(initialValues.management ?? '');
      setSelectedSuperintendency(initialValues.superintendency ?? '');
      setSelectedArea(initialValues.area ?? '');
      setServiceReasonText(initialValues.service_reason_text ?? '');
      setPreservedServiceReason(initialValues.service_reason ?? null);
    }
  }, [initialValues]);

  useEffect(() => {
    if (
      selectedManagement === '' ||
      selectedSuperintendency === '' ||
      selectedArea === ''
    ) {
      setSelectedShortcut('');
      return;
    }
    const match = elAbraGroups.find(
      g =>
        g.management === selectedManagement &&
        g.superintendency === selectedSuperintendency &&
        g.area === selectedArea
    );
    setSelectedShortcut(match?.id ?? '');
  }, [selectedManagement, selectedSuperintendency, selectedArea, elAbraGroups]);

  const selectedAreaItem = areas.find(a => a.id === selectedArea);

  const groupOptions = useMemo(
    () =>
      [...elAbraGroups]
        .sort((a, b) => compareEs(a.display_name, b.display_name))
        .map(g => ({
          value: g.id,
          label: g.area_responsible?.trim()
            ? `${g.display_name} — ${g.area_responsible.trim()}`
            : g.display_name,
        })),
    [elAbraGroups]
  );

  const managementOptions = useMemo(
    () =>
      [...managements]
        .sort((a, b) => compareEs(a.name, b.name))
        .map(m => ({ value: m.id, label: m.name })),
    [managements]
  );

  const superintendencyOptions = useMemo(
    () =>
      [...superintendencies]
        .sort((a, b) => compareEs(a.name, b.name))
        .map(s => ({ value: s.id, label: s.name })),
    [superintendencies]
  );

  const areaOptions = useMemo(
    () =>
      [...areas]
        .sort((a, b) => compareEs(a.name, b.name))
        .map(a => ({
          value: a.id,
          label: `${a.name}${a.area_responsible ? ` (${a.area_responsible})` : ''}`,
        })),
    [areas]
  );

  const findMatchingGroup = useCallback(() => {
    if (
      selectedManagement === '' ||
      selectedSuperintendency === '' ||
      selectedArea === ''
    ) {
      return null;
    }
    return (
      elAbraGroups.find(
        g =>
          g.management === selectedManagement &&
          g.superintendency === selectedSuperintendency &&
          g.area === selectedArea
      ) ?? null
    );
  }, [selectedManagement, selectedSuperintendency, selectedArea, elAbraGroups]);

  const groupFieldsComplete =
    selectedManagement !== '' &&
    selectedSuperintendency !== '' &&
    selectedArea !== '';

  const matchingGroup = findMatchingGroup();

  const reloadGroups = useCallback(
    (selectId?: number) => {
      listElAbraGroups(1, {
        onSuccess: res => {
          handleList(res, 'el_abra_groups');
          if (selectId !== undefined) setSelectedShortcut(selectId);
        },
      } as AdicionalEvents);
    },
    [listElAbraGroups, handleList]
  );

  const saveGroupIfNew = useCallback(
    (
      payload: { name?: string; management: number; superintendency: number; area: number },
      options: { silent?: boolean; onDone?: () => void } = {}
    ) => {
      const duplicate = elAbraGroups.find(
        g =>
          g.management === payload.management &&
          g.superintendency === payload.superintendency &&
          g.area === payload.area
      );
      if (duplicate) {
        setSelectedShortcut(duplicate.id);
        if (!options.silent) {
          showToast('Este grupo El Abra ya está registrado', 'info');
        }
        options.onDone?.();
        return;
      }

      storeElAbraGroup(payload, {
        onSuccess: r => {
          const newId = typeof r?.data?.id === 'number' ? r.data.id : undefined;
          reloadGroups(newId);
          if (!options.silent) {
            showToast('Grupo El Abra guardado correctamente', 'success');
          }
          options.onDone?.();
        },
        onError: r => {
          showToast(r?.message ?? 'No se pudo guardar el grupo El Abra', 'error');
          options.onDone?.();
        },
      });
    },
    [elAbraGroups, storeElAbraGroup, reloadGroups]
  );

  const handleShortcutChange = (value: string) => {
    if (value === '') {
      setSelectedShortcut('');
      return;
    }
    const groupId = Number(value);
    const group = elAbraGroups.find(g => g.id === groupId);
    if (!group) return;
    setSelectedShortcut(groupId);
    setSelectedManagement(group.management);
    setSelectedSuperintendency(group.superintendency);
    setSelectedArea(group.area);
  };

  /** Solo guarda los datos adicionales del servicio. No crea grupos. */
  const handleGuardar = () => {
    const payload = {
      management: selectedManagement === '' ? null : selectedManagement,
      superintendency: selectedSuperintendency === '' ? null : selectedSuperintendency,
      area: selectedArea === '' ? null : selectedArea,
      service_reason: preservedServiceReason,
      service_reason_text: serviceReasonText.trim() || null,
    };
    updateAdicional(serviceId, payload, {
      onSuccess: () => {
        showToast('Datos adicionales del servicio guardados correctamente', 'success');
        onSaved?.();
      },
      onError: () => showToast('Error al guardar datos adicionales', 'error'),
    });
  };

  /** Solo registra la combinación como grupo reutilizable. No guarda el servicio. */
  const handleSaveGroupClick = () => {
    if (!groupFieldsComplete) {
      showToast('Complete gerencia, superintendencia y área antes de guardar el grupo', 'error');
      return;
    }
    if (matchingGroup) {
      setSelectedShortcut(matchingGroup.id);
      showToast('Este grupo El Abra ya está registrado', 'info');
      return;
    }
    setGroupModalOpen(true);
  };

  const handleGroupModalSubmit = (payload: {
    name?: string;
    management: number;
    superintendency: number;
    area: number;
  }) => {
    saveGroupIfNew(payload, {
      onDone: () => setGroupModalOpen(false),
    });
  };

  const loading =
    fetching.managements ||
    fetching.superintendencies ||
    fetching.areas ||
    fetching.elAbraGroups;
  const saving = servicesFetching.updateAdicional;

  return (
    <div className="service-adicional-panel">
      {/* <p className="adicional-service-hint">
        Use un <strong>grupo El Abra</strong> para completar gerencia, superintendencia y área de una
        vez, o selecciónelos manualmente.
        <br />
        <strong>Registrar grupo</strong> solo guarda la combinación en el catálogo (no afecta al
        servicio). <strong>Guardar</strong> solo guarda los datos adicionales de este servicio.
      </p> */}

      <div className="adicional-grid">
        <div className="adicional-block adicional-block--group">
          <div className="adicional-block-row">
            <BaseSelect
              label="Grupo El Abra"
              name="el_abra_group"
              value={selectedShortcut === '' ? '' : String(selectedShortcut)}
              onChange={e => handleShortcutChange(e.target.value)}
              placeholder="— Seleccionar grupo —"
              preserveCase
              searchable
              disabled={loading}
              options={groupOptions}
            />
            <button
              type="button"
              className="adicional-add-btn adicional-add-btn--outline"
              onClick={handleSaveGroupClick}
              disabled={loading || fetching.storeElAbraGroup || !groupFieldsComplete}
              title="Registra la combinación actual como grupo reutilizable. No guarda el servicio."
            >
              Registrar grupo
            </button>
          </div>
          {matchingGroup && groupFieldsComplete && (
            <p className="adicional-group-note">Esta combinación ya está registrada como grupo.</p>
          )}
          {canRegisterGroupHint(groupFieldsComplete, matchingGroup) && (
            <p className="adicional-group-hint">
              Combinación nueva: puede registrarla como grupo o solo guardar el servicio.
            </p>
          )}
        </div>

        <div className="adicional-fields-row">
          <div className="adicional-block">
            <BaseSelect
              label="Gerencia"
              name="management"
              value={selectedManagement === '' ? '' : String(selectedManagement)}
              onChange={e => setSelectedManagement(e.target.value === '' ? '' : Number(e.target.value))}
              placeholder="— Seleccionar —"
              preserveCase
              searchable
              disabled={loading}
              options={managementOptions}
            />
          </div>

          <div className="adicional-block">
            <BaseSelect
              label="Superintendencia"
              name="superintendency"
              value={selectedSuperintendency === '' ? '' : String(selectedSuperintendency)}
              onChange={e =>
                setSelectedSuperintendency(e.target.value === '' ? '' : Number(e.target.value))
              }
              placeholder="— Seleccionar —"
              preserveCase
              searchable
              disabled={loading}
              options={superintendencyOptions}
            />
          </div>

          <div className="adicional-block">
            <BaseSelect
              label="Área"
              name="area"
              value={selectedArea === '' ? '' : String(selectedArea)}
              onChange={e => setSelectedArea(e.target.value === '' ? '' : Number(e.target.value))}
              placeholder="— Seleccionar —"
              preserveCase
              searchable
              disabled={loading}
              options={areaOptions}
            />
          </div>

          <div className="adicional-block">
            <label className="adicional-block-label">Responsable del área</label>
            <div className="adicional-readonly-field">
              {selectedAreaItem?.area_responsible?.trim() || '—'}
            </div>
          </div>

          <div className="adicional-block adicional-block--motivo">
            <label className="adicional-block-label">Motivo del servicio</label>
            <textarea
              className="adicional-textarea adicional-textarea--compact"
              value={serviceReasonText}
              onChange={e => setServiceReasonText(e.target.value)}
              placeholder="Describa el motivo del servicio…"
              rows={2}
              disabled={loading || saving}
              maxLength={8000}
            />
          </div>
        </div>
      </div>

      {loading && <div className="adicional-loading">Cargando catálogos…</div>}

      <div className="adicional-actions">
        <button
          type="button"
          className="adicional-guardar-btn"
          onClick={handleGuardar}
          disabled={loading || saving}
          title="Guarda gerencia, superintendencia, área y motivo en este servicio. No crea grupos."
        >
          {saving ? 'Guardando…' : 'Guardar'}
        </button>
        <span className="adicional-actions-hint">Solo datos de este servicio</span>
      </div>

      <ElAbraGroupModal
        isOpen={groupModalOpen}
        onClose={() => setGroupModalOpen(false)}
        onSubmit={handleGroupModalSubmit}
        loading={fetching.storeElAbraGroup}
        managements={managements}
        superintendencies={superintendencies}
        areas={areas}
        initialValues={
          groupFieldsComplete
            ? {
                management: selectedManagement as number,
                superintendency: selectedSuperintendency as number,
                area: selectedArea as number,
              }
            : null
        }
      />
    </div>
  );
};

function canRegisterGroupHint(
  groupFieldsComplete: boolean,
  matchingGroup: ElAbraGroupItem | null
): boolean {
  return groupFieldsComplete && !matchingGroup;
}

export default ServiceAdicionalPanel;
